import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { PortfolioFiltersToolbar } from '../filters/portfolioFiltersToolbar';
import { usePortfolioTableState } from '../hooks/usePortfolioTableState';
import { BOLD_SX, BORDERED_BOX_SX } from '../styles';
import { VIEW_MODE_TOGGLES } from '../utils/constants';
import BulkTradeStaging from './bulkTradeStaging';
import CrossPortfolioExposure from './crossPortfolioExposure';
import ModelPortfolioView from './modelPortfolioView';
import { PortfoliosTabFatalError } from './portfoliosTabFatalError';
import { PortfoliosTableView } from './portfoliosTableView';
import { PortfoliosTabSkeleton } from './portfoliosTabSkeleton';
import ReviewAndCommit from './reviewAndCommit';

const PortfoliosTab = () => {
  const theme = useTheme();
  const { t } = useTranslation('portfolioTradingHub');
  const {
    loading,
    hasRows,
    fatalErrorMessage,
    toolbarProps,
    tableProps,
    shellProps,
    selectionBarProps,
    reviewCommitProps,
    modelBulkStagingProps,
    exposureViewProps,
    modelViewProps,
    viewMode,
    density,
  } = usePortfolioTableState();

  if (loading && !hasRows) {
    return <PortfoliosTabSkeleton />;
  }

  if (fatalErrorMessage && !hasRows) {
    return <PortfoliosTabFatalError message={fatalErrorMessage} />;
  }

  return (
    <Box>
      {selectionBarProps && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            px: 1.5,
            py: 0.75,
            border: '1px solid',
            borderColor: theme.color.primary,
            borderRadius: '4px',
            bgcolor: theme.color.primaryVariant,
          }}
        >
          <Typography variant='labelSmall'>
            <strong>{selectionBarProps.selectedCount}</strong> portfolio
            {selectionBarProps.selectedCount > 1 ? 's' : ''} selected
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
            }}
          >
            <Button
              variant='contained'
              size='small'
              onClick={selectionBarProps.onRebalanceSelected}
            >
              Rebalance Selected
            </Button>
            <Button
              variant='outlined'
              size='small'
              onClick={selectionBarProps.onDownloadSelected}
            >
              {t('filters.downloadAllSelected')}
            </Button>
            <Button
              variant='text'
              size='small'
              onClick={selectionBarProps.onClear}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}
      {reviewCommitProps && (
        <Box
          sx={{
            mb: 1,
          }}
        >
          <ReviewAndCommit
            selectedPortfolios={reviewCommitProps.selectedPortfolios}
            organizationId={reviewCommitProps.organizationId}
            bulkTradingProcess={reviewCommitProps.bulkTradingProcess}
            onTradesCreated={reviewCommitProps.onTradesCreated}
            onCancel={reviewCommitProps.onCancel}
          />
        </Box>
      )}

      <Box
        sx={{
          ...BORDERED_BOX_SX,
          borderRadius: '4px 4px 0 0',
          bgcolor: theme.color.surface,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            px: 1,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: theme.color.outlineVariant,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              mr: 2,
              ...BORDERED_BOX_SX,
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          >
            {VIEW_MODE_TOGGLES.map((mode) => (
              <Box
                key={mode.key}
                onClick={() => shellProps.onViewModeChange(mode.key)}
                sx={{
                  px: 1.25,
                  py: 0.25,
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor:
                    shellProps.viewMode === mode.key
                      ? theme.color.primary
                      : 'transparent',
                  '&:hover': {
                    bgcolor:
                      shellProps.viewMode === mode.key
                        ? theme.color.primary
                        : theme.color.neutralHover,
                  },
                  transition: 'all 0.1s',
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...BOLD_SX,
                    textTransform: 'uppercase',
                    color:
                      shellProps.viewMode === mode.key
                        ? theme.color.onPrimary
                        : theme.color.onSurfaceVariant,
                  }}
                >
                  {mode.getLabel(shellProps.filteredCount)}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              flex: 1,
            }}
          />
        </Box>

        <PortfolioFiltersToolbar {...toolbarProps} />
      </Box>

      <Box
        sx={{
          ...BORDERED_BOX_SX,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          bgcolor: theme.color.surface,
          overflowX: 'auto',
        }}
      >
        {viewMode === 'portfolios' ? (
          <PortfoliosTableView {...tableProps} />
        ) : viewMode === 'exposure' ? (
          <CrossPortfolioExposure {...exposureViewProps} density={density} />
        ) : (
          <ModelPortfolioView
            models={modelViewProps.models}
            searchTerm={modelViewProps.searchTerm}
            onRebalanceSelected={modelViewProps.onRebalanceSelected}
          />
        )}
      </Box>

      {modelBulkStagingProps && <BulkTradeStaging {...modelBulkStagingProps} />}
    </Box>
  );
};

export default PortfoliosTab;
